import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { TextField } from "@material-ui/core";

import { Dropdown } from "js/Components/Dropdown";
import { MenuItem } from "js/Components/Menu";

import { guessUserCountry, getPostalCodeProps, getAllCountryCodes } from "../../utils/countries";
import type { AddressFormProps } from "./AddressForm";

const AddressContainer = styled.div<{ hasError?: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;
    gap: 10px;
    ${({ hasError }) => hasError && `
        padding-bottom: 10px;
    `}
`;

const InputContainer = styled.div<{ hasError?: boolean }>`
    position: relative;
    display: flex;
    flex: 1;
    height: 50px;
    min-height: 50px;
    min-width: 1px;
`;

const FieldBox = styled.div<{ hasValue?: boolean; hasError?: boolean }>`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    padding: 8px 10px;
    position: relative;
    border-radius: 1px;
    width: 100%;
    height: 100%;

    border-radius: 1px;
    transition: border 0.15s ease, box-shadow 0.15s ease;

    border: 2px solid ${({ hasError }) =>
        hasError
            ? "#ff0000"
            : "rgba(238, 238, 238, 0.85)"};

    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);

    &:hover {
        border-color: ${({ hasError }) => !hasError ? "#ddd" : ""};
    }
    
    &:focus-within {
        border-color: ${({ hasError }) => hasError ? "#ff0000" : "#11a9e2"};
    }
`;

const FloatingLabel = styled.label<{ hasValue?: boolean }>`
    position: absolute;
    left: 10px;
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: 400;
    line-height: 1;
    color: #666;
    background: white;
    padding: 0;
    transition: all 0.2s ease;
    pointer-events: none;
    z-index: 1;

    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;

    ${({ hasValue }) => hasValue && `
        top: 6px;
        transform: translateY(0);
        font-size: 12px;
    `}

    ${FieldBox}:focus-within & {
        top: 6px;
        transform: translateY(0);
        font-size: 12px;
    }
`;

const DropdownIcon = styled.div`
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    position: relative;
    
    &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid #888888;
    }
`;

const StyledDropdown = styled(Dropdown) <{ hasValue?: boolean }>`
    &&& {
        width: 100%;
        height: 100%;
        flex: 1;
        min-width: 0;

        .MuiSelect-select {
            padding: ${({ hasValue }) => hasValue ? "20px 0 4px 0" : "0"};
            font-size: 14px;
            font-weight: 400;
            color: #666;
            font-family: 'Source Sans Pro', sans-serif;
            line-height: 1.5;
            border: none;
            background: transparent;
            display: flex;
            align-items: center;
            min-height: auto;
        }

        .MuiOutlinedInput-root {
            border: none;
            padding: 0;
            height: 100%;
        }

        .MuiOutlinedInput-notchedOutline {
            border: none;
        }

        .MuiSelect-icon {
            display: none;
        }

        &.Mui-focused {
            .MuiOutlinedInput-notchedOutline {
                border: none;
            }
        }
    }
`;

const StyledTextField = styled(TextField) <{ warning: boolean; hasValue?: boolean }>`
    &&& {
        width: 100%;
        height: 100%;

        .MuiInputBase-input {
            padding: ${({ hasValue, focused }) => (hasValue || focused) ? "20px 0 4px 0" : "0"};
            font-size: 14px;
            font-weight: 400;
            color: #666;
            font-family: 'Source Sans Pro', sans-serif;
            line-height: 1.5;
            border: none;
            background: transparent;
            display: flex;
            align-items: center;
            min-height: auto;
        }
        .MuiInputBase-root {
            height: 100%;
            &::before, &::after {
                display: none !important;
            }
        }
        .MuiOutlinedInput-notchedOutline {
            border: none !important;
        }
    }
`;

const ErrorMessage = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    font-family: "Source Sans Pro", sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.25;
    color: #df1b41;
    background: white;
    padding: 2px 4px;
    border-radius: 2px;
    z-index: 1;
`;

export function AddressForm({ initialAddress, onChange, disabled, nextFieldRef, elements }: AddressFormProps) {
    const { t } = useTranslation();
    const [countryCode, setCountryCode] = useState(initialAddress.country || guessUserCountry());
    const [postalCode, setPostalCode] = useState(initialAddress.postal_code);

    const countryContainerRef = useRef<HTMLDivElement>(null);
    const zipCodeInputRef = useRef<HTMLInputElement>(null);

    const countryCodes = getAllCountryCodes();
    const postalCodeProps = getPostalCodeProps(countryCode);
    const isAddressValid = postalCodeProps.validate(postalCode);

    const postalCodeHasError = !isAddressValid && postalCode.length > 0;

    useEffect(() => {
        onChange({
            address: {
                country: countryCode,
                postal_code: postalCodeProps.show ? postalCode : ""
            },
            isAddressValid,
        });
    }, [countryCode, postalCode, postalCodeProps.show]);

    useEffect(() => {
        const nextFieldNode = (nextFieldRef && nextFieldRef.current) ? (ReactDOM.findDOMNode(nextFieldRef.current) as HTMLElement) : null;

        const handleBackTab = (e: KeyboardEvent) => {
            if (e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                if (countryContainerRef.current) {
                    const focusableElement = countryContainerRef.current.querySelector('[role="button"], [tabindex="0"], select, input') as HTMLElement;
                    if (focusableElement) {
                        focusableElement.focus();
                    }
                }
            }
        };

        if (nextFieldNode) {
            nextFieldNode.addEventListener('keydown', handleBackTab);
        }

        return () => {
            if (nextFieldNode) {
                nextFieldNode.removeEventListener('keydown', handleBackTab);
            }
        };
    }, [nextFieldRef]);

    const handleCountryCodeChange = (countryCode: string) => {
        setCountryCode(countryCode);
        setPostalCode("");
    };

    const handlePostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPostalCode(postalCodeProps.normalize(event.target.value));
    };

    const handleZipKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            event.stopPropagation();
            if (event.shiftKey) {
                const paymentElement = elements?.getElement('payment')?.focus();

            } else {
                if (countryContainerRef.current) {
                    const focusableElement = countryContainerRef.current.querySelector('[role="button"], [tabindex="0"], select, input') as HTMLElement;
                    if (focusableElement) {
                        focusableElement.focus();
                    }
                }
            }
        }
    };

    const handleCountryKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab') {
            if (event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                // If zip code field exists, focus it
                if (zipCodeInputRef.current) {
                    zipCodeInputRef.current.focus();
                } else if (elements) {
                    // Otherwise (no zip code for this country), focus payment element
                    const paymentElement = elements.getElement('payment');
                    if (paymentElement) {
                        paymentElement.focus();
                    }
                }
            } else {
                event.preventDefault();
                event.stopPropagation();

                if (nextFieldRef && nextFieldRef.current) {
                    const domNode = ReactDOM.findDOMNode(nextFieldRef.current) as HTMLElement;

                    if (domNode && typeof domNode.focus === "function") {
                        domNode.focus();
                    }
                }
            }
        }
    };

    return (
        <AddressContainer hasError={postalCodeHasError}>
            {postalCodeProps.show && (
                <InputContainer hasError={postalCodeHasError}>
                    <FieldBox hasValue={!!postalCode} hasError={postalCodeHasError}>
                        <FloatingLabel hasValue={!!postalCode}>
                            {postalCodeProps.name}
                        </FloatingLabel>
                        <StyledTextField
                            placeholder=""
                            onChange={handlePostalCodeChange}
                            value={postalCode}
                            fullWidth
                            disabled={disabled}
                            autoFocus={false}
                            autoComplete="off"
                            warning={postalCodeHasError}
                            hasValue={!!postalCode}
                            data-testid="minimal-billing-address-form-postal-code"
                            onKeyDownCapture={handleZipKeyDownCapture}
                            inputRef={zipCodeInputRef}
                        />
                    </FieldBox>
                    {postalCodeHasError && <ErrorMessage>{t("Invalid {{field}}", { field: postalCodeProps.name })}</ErrorMessage>}
                </InputContainer>
            )}
            <InputContainer ref={countryContainerRef}>
                <FieldBox hasValue={true}>
                    <FloatingLabel hasValue={true}>
                        {t("Country")}
                    </FloatingLabel>
                    <StyledDropdown
                        value={countryCode}
                        onChange={handleCountryCodeChange}
                        disabled={disabled}
                        hasValue={true}
                        data-testid="minimal-billing-address-form-country"
                        onKeyDownCapture={handleCountryKeyDownCapture}
                    >
                        {countryCodes.map(({ code, name }) => (
                            <MenuItem key={code} value={code.toUpperCase()}>{name}</MenuItem>
                        ))}
                    </StyledDropdown>
                    <DropdownIcon />
                </FieldBox>
            </InputContainer>
        </AddressContainer>
    );
}
